import axios from 'axios';
import { API_BASE_URL } from '../config/api';

export interface PreviewMetrics {
  engagement: number;
  tension: number;
  clarity: number;
  pacing: number;
  retention: number;
}

export interface PreviewOption {
  id: string;
  name: string;
  description: string;
  metrics: PreviewMetrics;
}

export interface ContentAnalysis {
  dominantThemes: string[];
  suggestedDuration: number;
  recommendedOption: string;
}

export interface PreviewMetricsData {
  generatedAt: string;
  contentAnalysis: ContentAnalysis;
  options: PreviewOption[];
}

export interface PreviewJobStatus {
  jobId: string;
  status: 'queued' | 'processing' | 'completed' | 'failed';
  percentage: number;
  message: string;
  stage?: string;
  error?: string;
}

export interface GeneratePreviewRequest {
  projectId: string;
  contentType?: 'podcast' | 'vlog' | 'interview' | 'tutorial' | 'presentation' | 'other';
  language?: string;
}

export interface GeneratePreviewResponse {
  jobId: string;
}

/**
 * Start preview generation for a project
 */
export const startPreviewGeneration = async (
  request: GeneratePreviewRequest
): Promise<GeneratePreviewResponse> => {
  const response = await axios.post(
    `${API_BASE_URL}/video-preview/generate`,
    request,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    }
  );
  return response.data;
};

/**
 * Get job status for preview generation
 */
export const getPreviewJobStatus = async (
  jobId: string
): Promise<PreviewJobStatus> => {
  const response = await axios.get(
    `${API_BASE_URL}/video-preview/status/${jobId}`,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    }
  );
  return response.data;
};

/**
 * Get preview metrics for a project
 */
export const getPreviewMetrics = async (
  projectId: string
): Promise<PreviewMetricsData | null> => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/video-preview/metrics/${projectId}`,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return null;
    }
    throw error;
  }
};

/**
 * Clear preview segments and metrics
 */
export const clearPreviewData = async (projectId: string): Promise<void> => {
  await axios.post(
    `${API_BASE_URL}/video-preview/clear/${projectId}`,
    {},
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    }
  );
};
